import React, { useState, useEffect, useMemo, useCallback } from "react";
import Papa from "papaparse";
import {
  Minus,
  Plus,
  ChevronDown,
  Check,
  RefreshCw,
  MessageCircle,
  X,
  Loader2,
  Shirt,
  AlertTriangle,
  Coins,
  Sparkles,
  Ban,
} from "lucide-react";

/* ============================================================================
   SETUP — paste your published Google Sheet CSV export links directly below.
   File > Share > Publish to web > pick each tab > CSV, then paste the link.
   There is intentionally NO front-end UI to edit these — tele-callers should
   never be able to change the data source. Edit this file and redeploy
   whenever a link changes. The app falls back to demo data instantly while
   it fetches the live sheet in the background, and keeps quietly retrying
   for a few seconds if the first attempt doesn't land — no manual reload
   needed.

   Products tab now needs 3 columns: ItemName, BasePrice, AbsoluteFloorPrice
   (AbsoluteFloorPrice = the lowest profitable per-unit price — used by
   Haggle Mode below, and never shown directly to the rep).

   New tab — HaggleConfig (1 row, 2 columns): ConcessionDivisor, RoundingIncrement
   e.g.  ConcessionDivisor=3, RoundingIncrement=50
   This controls how much of the available margin Haggle Mode is allowed
   to release. Change it centrally in the sheet — no code edits needed.
============================================================================ */
const DEFAULT_SHEET_URLS = {
  products:
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vQx5O24dgKxt6rn2E1qmAqfCXqth4yiX2J0doNz0Qy_Ee_8TM9dUxmfl7yU5cTUV_c-JPod2WrJ2DJv/pub?gid=514338300&single=true&output=csv",
  printCosts:
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vQx5O24dgKxt6rn2E1qmAqfCXqth4yiX2J0doNz0Qy_Ee_8TM9dUxmfl7yU5cTUV_c-JPod2WrJ2DJv/pub?gid=627346877&single=true&output=csv",
  discountRules:
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vQx5O24dgKxt6rn2E1qmAqfCXqth4yiX2J0doNz0Qy_Ee_8TM9dUxmfl7yU5cTUV_c-JPod2WrJ2DJv/pub?gid=1933025148&single=true&output=csv",
  haggleConfig:
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vQx5O24dgKxt6rn2E1qmAqfCXqth4yiX2J0doNz0Qy_Ee_8TM9dUxmfl7yU5cTUV_c-JPod2WrJ2DJv/pub?gid=28468062&single=true&output=csv",
};

const DEMO_PRODUCTS = [
  { ItemName: "Basic T-Shirt", BasePrice: 180, AbsoluteFloorPrice: 110 },
  { ItemName: "Premium Polo", BasePrice: 320, AbsoluteFloorPrice: 200 },
  { ItemName: "Chef Coat", BasePrice: 450, AbsoluteFloorPrice: 280 },
  { ItemName: "Hoodie", BasePrice: 550, AbsoluteFloorPrice: 350 },
];

const DEMO_PRINT_COSTS = [
  { PrintType: "Left Chest Logo", CostOneSide: 25, CostBothSides: 45 },
  { PrintType: "A4 Size", CostOneSide: 60, CostBothSides: 110 },
  { PrintType: "A3 Size", CostOneSide: 90, CostBothSides: 160 },
];

const DEMO_DISCOUNT_RULES = [
  { MinQty: 1, MaxQty: 49, DiscountPercentage: 0, FestivalModifier: 0 },
  { MinQty: 50, MaxQty: 99, DiscountPercentage: 5, FestivalModifier: 2 },
  { MinQty: 100, MaxQty: 249, DiscountPercentage: 10, FestivalModifier: 2 },
  { MinQty: 250, MaxQty: 999999, DiscountPercentage: 15, FestivalModifier: 2 },
];

const DEMO_HAGGLE_CONFIG = [{ ConcessionDivisor: 3, RoundingIncrement: 50 }];

function toNumber(v, fallback = 0) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

function formatINR(n) {
  if (!Number.isFinite(n)) return "₹0";
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function roundToIncrement(n, increment = 50) {
  const step = increment > 0 ? increment : 50;
  return Math.round(n / step) * step;
}

/* Fetches a published Google Sheet CSV tab using fetch() + Papa.parse(text).
   This is noticeably faster and more reliable than Papa's built-in
   download mode on some networks/hosts, because we control the timeout
   and retry behaviour directly instead of waiting on a hanging XHR.
   Resolves to null on failure so the caller can fall back to demo data. */
async function fetchSheetTab(url, { retries = 2, timeoutMs = 6000 } = {}) {
  if (!url) return null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);

      const res = await fetch(url, {
        signal: controller.signal,
        cache: "no-store",
      });
      clearTimeout(timer);

      if (!res.ok) throw new Error("HTTP " + res.status);

      const text = await res.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });

      if (parsed.data && parsed.data.length > 0) {
        return parsed.data;
      }
      throw new Error("Empty CSV");
    } catch (err) {
      if (attempt === retries) return null;
      // brief backoff before retrying
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  return null;
}

export default function PricingCalculator() {
  // Sheet URLs are configured directly in code (DEFAULT_SHEET_URLS above) —
  // no front-end UI for editing them, so tele-callers can't accidentally
  // change the data source.
  const sheetUrls = DEFAULT_SHEET_URLS;

  const [products, setProducts] = useState(DEMO_PRODUCTS);
  const [printCosts, setPrintCosts] = useState(DEMO_PRINT_COSTS);
  const [discountRules, setDiscountRules] = useState(DEMO_DISCOUNT_RULES);
  const [haggleConfig, setHaggleConfig] = useState(DEMO_HAGGLE_CONFIG);

  const [usingDemoData, setUsingDemoData] = useState(true);
  const [loading, setLoading] = useState(false);

  const [productName, setProductName] = useState(DEMO_PRODUCTS[0].ItemName);
  const [quantity, setQuantity] = useState(50);
  const [printType, setPrintType] = useState(DEMO_PRINT_COSTS[0].PrintType);
  const [placement, setPlacement] = useState("one"); // "one" | "both"

  const [copied, setCopied] = useState(false);
  const [showHaggleModal, setShowHaggleModal] = useState(false);

  const loadSheets = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      const [p, pc, dr, hc] = await Promise.all([
        fetchSheetTab(sheetUrls.products),
        fetchSheetTab(sheetUrls.printCosts),
        fetchSheetTab(sheetUrls.discountRules),
        fetchSheetTab(sheetUrls.haggleConfig),
      ]);

      const gotAny = p || pc || dr || hc;

      setProducts(p && p.length ? p : DEMO_PRODUCTS);
      setPrintCosts(pc && pc.length ? pc : DEMO_PRINT_COSTS);
      setDiscountRules(dr && dr.length ? dr : DEMO_DISCOUNT_RULES);
      setHaggleConfig(hc && hc.length ? hc : DEMO_HAGGLE_CONFIG);
      setUsingDemoData(!gotAny);
      if (!silent) setLoading(false);
      return gotAny;
    },
    [sheetUrls]
  );

  // Initial load — demo data renders instantly (it's the default state),
  // then this swaps in live sheet data the moment it's ready.
  useEffect(() => {
    loadSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the first attempt didn't get live data (slow network, sheet still
  // warming up, etc.), keep quietly retrying in the background every few
  // seconds — instead of making the tele-caller manually reload the page.
  useEffect(() => {
    if (!usingDemoData) return;
    let attempts = 0;
    const id = setInterval(async () => {
      attempts += 1;
      const gotData = await loadSheets(true); // silent = no spinner flicker
      if (gotData || attempts >= 8) clearInterval(id);
    }, 3000);
    return () => clearInterval(id);
  }, [usingDemoData, loadSheets]);

  // Keep selections valid if the underlying sheet data changes
  useEffect(() => {
    if (products.length && !products.some((p) => p.ItemName === productName)) {
      setProductName(products[0].ItemName);
    }
  }, [products, productName]);

  useEffect(() => {
    if (printCosts.length && !printCosts.some((p) => p.PrintType === printType)) {
      setPrintType(printCosts[0].PrintType);
    }
  }, [printCosts, printType]);

  const calc = useMemo(() => {
    const product = products.find((p) => p.ItemName === productName) || products[0];
    const print = printCosts.find((p) => p.PrintType === printType) || printCosts[0];

    const basePrice = toNumber(product?.BasePrice);
    const printCost =
      placement === "one"
        ? toNumber(print?.CostOneSide)
        : toNumber(print?.CostBothSides);

    const unitBeforeDiscount = basePrice + printCost;
    const baseTotal = unitBeforeDiscount * quantity;

    const rule = discountRules.find(
      (r) => quantity >= toNumber(r.MinQty) && quantity <= toNumber(r.MaxQty, Infinity)
    );

    const volumeDiscount = toNumber(rule?.DiscountPercentage);
    const festivalModifier = toNumber(rule?.FestivalModifier);
    const totalDiscountPct = volumeDiscount + festivalModifier;

    const finalPerUnit = unitBeforeDiscount * (1 - totalDiscountPct / 100);
    const totalOrderValue = finalPerUnit * quantity;
    const youSave = baseTotal - totalOrderValue;

    // --- Haggle Mode / Deal Closer math ---
    const floorPrice = toNumber(product?.AbsoluteFloorPrice);
    // Floor total = lowest profitable unit price + print cost, across the order
    const floorTotal = (floorPrice + printCost) * quantity;
    const maxBuffer = totalOrderValue - floorTotal;
    const canHaggle = maxBuffer > 0;

    const hConfig = haggleConfig?.[0] || {};
    const concessionDivisor = toNumber(hConfig.ConcessionDivisor, 3) || 3;
    const roundingIncrement = toNumber(hConfig.RoundingIncrement, 50) || 50;

    const smartConcession = canHaggle
      ? Math.max(0, roundToIncrement(maxBuffer / concessionDivisor, roundingIncrement))
      : 0;
    const haggleFinalTotal = totalOrderValue - smartConcession;
    const haggleFinalPerUnit = quantity > 0 ? haggleFinalTotal / quantity : 0;

    return {
      product,
      print,
      basePrice,
      printCost,
      unitBeforeDiscount,
      baseTotal,
      volumeDiscount,
      festivalModifier,
      totalDiscountPct,
      finalPerUnit,
      totalOrderValue,
      youSave,
      floorPrice,
      floorTotal,
      maxBuffer,
      canHaggle,
      smartConcession,
      haggleFinalTotal,
      haggleFinalPerUnit,
    };
  }, [products, printCosts, discountRules, haggleConfig, productName, printType, placement, quantity]);

  const adjustQty = (delta) => {
    setQuantity((q) => Math.max(1, q + delta));
  };

  const handleQtyInput = (e) => {
    const v = e.target.value.replace(/[^0-9]/g, "");
    setQuantity(v === "" ? 1 : Math.max(1, parseInt(v, 10)));
  };

  const buildWhatsAppText = () => {
    const placementLabel = placement === "one" ? "One Side" : "Front & Back";
    const lines = [
      "🏆 *BEST Discount on Your Order!*",
      "",
      `📦 Product: ${calc.product?.ItemName ?? "-"}`,
      `🔢 Quantity: ${quantity} pcs`,
      `🖨️ Print: ${calc.print?.PrintType ?? "-"} (${placementLabel})`,
      "",
    ];

    // Pricing section
    if (calc.totalDiscountPct > 0) {
      lines.push(`💵 Price Before Discount: ~${formatINR(calc.baseTotal)}~`);
      lines.push("");
      lines.push(`🎉 Discount: ${calc.totalDiscountPct}% OFF (Bulk${
          calc.festivalModifier > 0 ? " + Employee Discount" : ""
        })`);
    } else {
      lines.push(`💵 Price: ${formatINR(calc.baseTotal)}`);
    }
    lines.push(""); // Space after pricing section, before final price

    lines.push(`✅ *Final Price: ${formatINR(calc.finalPerUnit)} / pc*`);
    lines.push("");
    lines.push(`🧾 *Total Order Value: ${formatINR(calc.totalOrderValue)}*`);
    lines.push("");

    if (calc.youSave > 0) {
      lines.push(`💸 You Save: ${formatINR(calc.youSave)}`);
      lines.push("");
    }

    lines.push("🚚 _Delivery charges extra (as per location)_");
    lines.push("⏰ _Valid for today only_");

    return lines.join("\n");
  };

  const copyQuote = async () => {
    const text = buildWhatsAppText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex justify-center">
      <div className="w-full max-w-md bg-gray-50 flex flex-col min-h-screen relative">
        {/* Header */}
        <div className="bg-slate-900 text-white px-4 py-4 flex items-center justify-between sticky top-0 z-20 shadow-md">
          <div className="flex items-center gap-2">
            <div className="bg-white/10 rounded-lg p-2">
              <Shirt size={20} />
            </div>
            <div>
              <h1 className="font-bold text-base leading-tight">HL Price App</h1>
              <p className="text-xs text-slate-300 leading-tight">For sales team</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadSheets()}
              className="p-2 rounded-lg bg-white/10 active:bg-white/20"
              aria-label="Refresh pricing data"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <RefreshCw size={18} />
              )}
            </button>
          </div>
        </div>

        {usingDemoData && (
          <div className="mx-4 mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>APP IS NOT WORKING.. SERVER DOWN</span>
          </div>
        )}

        {/* Scrollable input cards */}
        <div className="flex-1 px-4 pt-4 pb-44 space-y-4">
          {/* Product Selection */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Product
            </label>
            <div className="relative">
              <select
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-xl px-4 py-4 text-lg font-medium text-slate-900 pr-10 focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {products.map((p) => (
                  <option key={p.ItemName} value={p.ItemName}>
                    {p.ItemName} — {formatINR(toNumber(p.BasePrice))}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={20}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Quantity */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm overflow-hidden">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Quantity
            </label>
            <div className="flex items-center gap-2 w-full">
              <button
                onClick={() => adjustQty(-1)}
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 text-slate-900"
                aria-label="Decrease quantity"
              >
                <Minus size={18} />
              </button>
              <input
                type="text"
                inputMode="numeric"
                value={quantity}
                onChange={handleQtyInput}
                className="flex-1 min-w-0 w-full text-center text-xl font-bold text-slate-900 bg-gray-50 border border-gray-200 rounded-xl py-2.5 px-1 focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <button
                onClick={() => adjustQty(1)}
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 text-slate-900"
                aria-label="Increase quantity"
              >
                <Plus size={18} />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {[25, 50, 100, 250].map((q) => (
                <button
                  key={q}
                  onClick={() => setQuantity(q)}
                  className={`py-2 rounded-lg text-xs sm:text-sm font-medium border truncate ${
                    quantity === q
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-gray-50 text-gray-600 border-gray-200"
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Print Size */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Print Size
            </label>
            <div className="relative">
              <select
                value={printType}
                onChange={(e) => setPrintType(e.target.value)}
                className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-xl px-4 py-4 text-lg font-medium text-slate-900 pr-10 focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {printCosts.map((p) => (
                  <option key={p.PrintType} value={p.PrintType}>
                    {p.PrintType}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={20}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Print Placement */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Print Placement
            </label>
            <div className="grid grid-cols-2 gap-2 bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setPlacement("one")}
                className={`py-3 rounded-lg text-base font-semibold transition-colors ${
                  placement === "one"
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-gray-600"
                }`}
              >
                One Side
              </button>
              <button
                onClick={() => setPlacement("both")}
                className={`py-3 rounded-lg text-base font-semibold transition-colors ${
                  placement === "both"
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-gray-600"
                }`}
              >
                Front &amp; Back
              </button>
            </div>
          </div>

          {/* Breakdown */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Breakdown</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Base price</span>
                <span>{formatINR(calc.basePrice)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>
                  Print cost ({placement === "one" ? "one side" : "front & back"})
                </span>
                <span>{formatINR(calc.printCost)}</span>
              </div>
              <div className="flex justify-between text-gray-700 font-medium pt-2 border-t border-gray-100">
                <span>Unit price before discount</span>
                <span>{formatINR(calc.unitBeforeDiscount)}</span>
              </div>
              {calc.volumeDiscount > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Volume discount</span>
                  <span>− {calc.volumeDiscount}%</span>
                </div>
              )}
              {calc.festivalModifier > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Festival modifier</span>
                  <span>− {calc.festivalModifier}%</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky results bar */}
        <div className="sticky bottom-0 z-20 px-4 pb-4 pt-3 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-400">Base total</span>
              <span className="text-xs text-gray-400 line-through">
                {formatINR(calc.baseTotal)}
              </span>
            </div>

            {calc.totalDiscountPct > 0 && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-emerald-600">
                  Discount Applied
                </span>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  − {calc.totalDiscountPct}% Approved
                </span>
              </div>
            )}

            <div className="flex items-end justify-between mb-3">
              <div>
                <p className="text-xs text-gray-500">Final price / unit</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatINR(calc.finalPerUnit)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total order value</p>
                <p className="text-3xl font-extrabold text-slate-900">
                  {formatINR(calc.totalOrderValue)}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowHaggleModal(true)}
              className="w-full flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 active:bg-gray-100 text-gray-600 text-sm font-medium py-3 px-3 rounded-xl mb-3">
              <span className="flex items-center gap-2">
                <Coins size={16} />
                (LAST PRICE)
              </span>
              <span className="text-xs text-slate-400">Tap to check</span>
            </button>

            <button
              onClick={copyQuote}
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 active:bg-emerald-700 text-white font-semibold py-4 rounded-xl text-base"
            >
              {copied ? (
                <>
                  <Check size={20} /> Copied to clipboard
                </>
              ) : (
                <>
                  <MessageCircle size={20} /> Copy Quote for WhatsApp
                </>
              )}
            </button>
          </div>
        </div>

        {/* Haggle Mode popup */}
        {showHaggleModal && (
          <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-sm bg-white rounded-2xl p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Coins size={18} />
                  FIANL PRICE CHECK 
                </h2>
                <button
                  onClick={() => setShowHaggleModal(false)}
                  className="p-1.5 rounded-lg bg-gray-100"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              {calc.canHaggle ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs font-bold px-3 py-1 rounded-full mb-3">
                    <Sparkles size={14} />
                    Final Lowest Price
                  </div>

                  <p className="text-sm text-gray-500 mb-1">
                    Itna Extra discount total par or de sakte ho agr client convince nahi ho raha hai to. (Final Price)
                  </p>
                  <p className="text-3xl font-extrabold text-indigo-600 mb-4">
                    {formatINR(calc.smartConcession)} OFF
                  </p>

                  <div className="bg-gray-50 rounded-xl p-3 mb-3">
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span>Current Price</span>
                      <span className="line-through">
                        {formatINR(calc.totalOrderValue)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-emerald-600">
                      <span>New Final Price</span>
                      <span>{formatINR(calc.haggleFinalTotal)}</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 italic mb-4">
                    Customer ko bol do ye final price hai.
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 text-xs font-bold px-3 py-1 rounded-full mb-3">
                    <Ban size={14} />
                    ALREADY BEST PRICE
                  </div>
                  <p className="text-lg font-bold text-red-600 mb-1">
                    🛑 Already Lowest Price hai..
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Isse order pe or discount nahi de sakte
                  </p>
                </div>
              )}

              <button
                onClick={() => setShowHaggleModal(false)}
                className="w-full bg-slate-900 text-white font-semibold py-3 rounded-xl"
              >
                Got it
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
